/**
 * The one shared Hiive bot. Anyone can tag @hiivebot in chats, posts and
 * comments; these aliases are reserved so no person can take them.
 */
export const HIIVE_BOT_HANDLE = "hiivebot";
export const HIIVE_BOT_NAME = "Hiive bot";
/**
 * The author id a bot reply is remapped to on read. The row itself is stored
 * under whoever asked, so this id never exists in the user table.
 */
export const HIIVE_BOT_AUTHOR_ID = "hiive-bot";
const HIIVE_BOT_ALIASES = new Set([HIIVE_BOT_HANDLE, "hivebot", "hiive_bot"]);
export function isHiiveBotHandle(handle: string | null | undefined) {
  return !!handle && HIIVE_BOT_ALIASES.has(handle.toLowerCase());
}
/** True when text tags the Hiive bot (@hiivebot or an alias). */
export function mentionsHiiveBot(text: string | null | undefined) {
  return (
    !!text && /(^|[^\w.@])@(hiivebot|hivebot|hiive_bot)(?!\w|\.\w)/i.test(text)
  );
}

/** Reserved routing names, independent of a human username's 30-character limit. */
export const ASSISTANT_HANDLE_PREFIX = "hiive_bot.";
const LEGACY_PREFIX = "bot.";
export const ASSISTANT_SUFFIX_MAX_LENGTH = 24;
export function isAssistantHandleNamespace(handle: string) {
  return (
    isHiiveBotHandle(handle) ||
    handle.startsWith(ASSISTANT_HANDLE_PREFIX) ||
    handle.startsWith(LEGACY_PREFIX)
  );
}
export function assistantHandleSuffix(handle: string) {
  return handle.replace(/^(?:hiive_bot|bot)\./, "");
}
export function canonicalAssistantHandle(handle: string) {
  if (isHiiveBotHandle(handle)) return HIIVE_BOT_HANDLE;
  return isAssistantHandleNamespace(handle)
    ? `${ASSISTANT_HANDLE_PREFIX}${assistantHandleSuffix(handle)}`
    : handle;
}
export function isValidAssistantHandle(handle: string) {
  return /^(?:hiive_bot|bot)\.[a-z][a-z0-9_]{2,23}$/.test(handle);
}
/**
 * The assistant tag is derived from the owner's username, never chosen.
 *
 * The username namespace allows `a.b` and `a_b`, which both collapse to the
 * same suffix here, so callers must still resolve collisions before saving.
 * Usernames can be as short as two characters; pad rather than dropping the
 * name so every account keeps a distinct tag.
 */
export function assistantHandleFromUsername(
  username: string | null | undefined,
): string {
  const safe = (username ?? "helper").toLowerCase().replace(/[^a-z0-9_]/g, "_");
  const prefixed = /^[a-z]/.test(safe) ? safe : `my_${safe}`;
  const suffix = prefixed.slice(0, ASSISTANT_SUFFIX_MAX_LENGTH);
  return suffix.length >= 3 ? suffix : suffix.padEnd(3, "_");
}
export function assistantHandleAliases(handle: string) {
  if (!isValidAssistantHandle(handle)) return [handle];
  const suffix = assistantHandleSuffix(handle);
  return [`${ASSISTANT_HANDLE_PREFIX}${suffix}`, `${LEGACY_PREFIX}${suffix}`];
}
